import React from 'react'
import styled from '@emotion/styled'
import type { ToastItem } from '@mobi/component-library/Feedback/Toast/types'
import type { globalToastEmitter } from '@mobi/component-library/src/Feedback/Toast/helpers/events'
import { ToastContent } from '@mobi/component-library/Feedback/Toast'

const enum LocalConstants {
  DefaultTimeoutMs = 3_000, // 3s
  FullWidth = 'js-toast--full-width',
}

export const Toast: React.FC<{
  toast: ToastItem
  onMount: OnMountFn
  toastEmitter: typeof globalToastEmitter
}> = React.memo(({ toast, onMount, toastEmitter }) => {
  const toastRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    onMount({ id: toast.id, ref: toastRef })
  }, [onMount, toast])

  React.useEffect(() => {
    if (toast.timeoutMs === Infinity) return

    const timerId = setTimeout(() => {
      toastEmitter.emit('removeToast', { id: toast.id })
    }, toast.timeoutMs || LocalConstants.DefaultTimeoutMs)

    return () => {
      clearTimeout(timerId)
    }
  }, [toast, toastEmitter])

  return (
    <ToastStyled ref={toastRef} className={`${toast.isFullWidth ? LocalConstants.FullWidth : ''} `}>
      {toast.component ? toast.component : <ToastContent {...toast} />}
    </ToastStyled>
  )
})

Toast.displayName = 'Toast'

// ======
// Styles
// ======

const ToastStyled = styled.div({
  position: 'relative',
  zIndex: 1,
  pointerEvents: 'all',

  animationName: 'toast-enter',
  animationFillMode: 'forwards',
  animationDuration: '300ms',
  animationTimingFunction: 'cubic-bezier(0.165, 0.84, 0.44, 1)',

  ['&.' + LocalConstants.FullWidth]: { alignSelf: 'stretch' },

  '@keyframes toast-enter': {
    '0%': { opacity: 0, transform: 'translateY(150%)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
})

// =====
// Types
// =====

type OnMountFn = (arg: { id: ToastItem['id']; ref: React.RefObject<HTMLDivElement> }) => void
